// بِسْمِ ٱللَّهِ ٱلرَّحْمَٰنِ ٱلرَّحِيمِ
//
// Post-lifecycle wiring for the Бриф promotion port — SPEC §2 of
// brief-port-spec.md. Covers create / list / get / save-draft / preflight /
// publish / reschedule / bulk / metrics against the v2-promotion-publisher
// worker, plus the client-derived `getQueueStage` bucket.
//
// Fetch strategy:
//   • WRITES + strict reads (create, get, save-draft, preflight, publish,
//     reschedule, bulk, metrics, run-metrics) → publisherCall (real errors
//     surface; 401 → global logout).
//   • The Queue/Calendar list read → softCall (mock fallback `[]`, never logs
//     out) so the ops screens keep demonstrating when the session is gone.
//
// Paths are passed verbatim — the worker rewrites `/api/publisher` →
// `/api/promotion`. There is NO response envelope (worker serialises the object
// directly); errors are `{ error }`. No idempotency keys anywhere.

import { publisherCall, softCall } from './client';
import type {
  ChannelReadiness,
  EngagementSample,
  Platform,
  PlatformDraft,
  PostStatus,
  PromotionPost,
  PublishResult,
  QueueStage,
  SourceContent,
} from './types';

// Platform iteration order — mirrors the old page so getQueueStage / status
// scans behave identically.
const PLATFORMS: Platform[] = [
  'telegram',
  'twitter',
  'instagram',
  'threads',
  'facebook',
  'linkedin',
  'youtube',
  'tiktok',
  'reddit',
  'producthunt',
];

// ── create (SPEC §2 · §1c chat variant) ──────────────────────────

/** A channel chip carried into create / suggestions / chat-plan requests. */
export interface SelectedChannelInput {
  id?: string;
  platform?: Platform;
  method?: 'direct' | 'buffer';
  label?: string;
  buffer_channel_id?: string;
}

/** Hermes creative envelope appended by the chat create variant (§1c). */
export interface HermesCreativeInput {
  enabled: true;
  route: 'hermes-codex-plugin';
  brief: string;
}

/**
 * POST `/api/publisher/create`. `source.title` + `source.body` are required by
 * the worker (→ 400 otherwise). `skip_ai:true` is the chat path (drafts already
 * rendered); omit it for the rule-based composer. Returns the freshly INSERTed
 * post (never idempotent).
 */
export interface CreatePostRequest {
  source: SourceContent;
  skip_ai?: boolean;
  scheduled_at?: string | null;
  enabled_platforms?: Platform[];
  drafts?: Record<Platform, PlatformDraft> | Partial<Record<Platform, PlatformDraft>>;
  platform_images?: Partial<Record<Platform, string>>;
  telegram_channels?: string[];
  buffer_channel_ids?: string[];
  selected_channels?: SelectedChannelInput[];
  hermes_creative?: HermesCreativeInput;
}

/**
 * Deterministic idempotency key from the request body (64-bit FNV-1a pair).
 * Identical create submissions (double-click, network retry, replay) hash to
 * the same key, so the worker collapses them into a single post.
 */
function idempotencyKeyFor(bodyString: string): string {
  let a = 0x811c9dc5;
  let b = 0xcbf29ce4;
  for (let i = 0; i < bodyString.length; i++) {
    const c = bodyString.charCodeAt(i);
    a = Math.imul(a ^ c, 0x01000193);
    b = Math.imul(b ^ c, 0x85ebca6b);
  }
  return (a >>> 0).toString(16).padStart(8, '0') + (b >>> 0).toString(16).padStart(8, '0');
}

export async function createPost(req: CreatePostRequest): Promise<PromotionPost> {
  const body = JSON.stringify(req);
  const json = await publisherCall<{ post?: PromotionPost; error?: string }>(
    '/api/publisher/create',
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Idempotency-Key': idempotencyKeyFor(body) },
      body,
    },
  );
  if (!json.post) throw new Error(json.error ?? 'create failed');
  return json.post;
}

// ── list (soft — Queue / Calendar) ───────────────────────────────

/**
 * GET `/api/publisher/list?limit=`. SOFT read: on any failure resolves to `[]`
 * (live:false) so the ops screens keep working. Worker caps limit at 200 and
 * returns created_at-desc. Queue/Calendar pass 200.
 */
export async function listPosts(limit = 50): Promise<PromotionPost[]> {
  const loaded = await softCall<PromotionPost[]>(
    `/api/publisher/list?limit=${limit}`,
    [],
    (json) => {
      const posts = (json as { posts?: unknown }).posts;
      return Array.isArray(posts) ? (posts as PromotionPost[]) : [];
    },
  );
  return loaded.data;
}

// ── get (strict — poll while publishing) ─────────────────────────

/**
 * GET `/api/publisher/:id`. STRICT read (404 → throw `not found`). Poll every
 * 3s while any platform sits in `publishing`.
 */
export async function getPost(id: string): Promise<PromotionPost> {
  const json = await publisherCall<{ post?: PromotionPost; error?: string }>(
    `/api/publisher/${id}`,
  );
  if (!json.post) throw new Error(json.error ?? 'not found');
  return json.post;
}

// ── save draft ───────────────────────────────────────────────────

export interface SaveDraftResult {
  post?: PromotionPost;
  draft?: PlatformDraft;
}

/**
 * POST `/api/publisher/:id/drafts/:platform` with a partial draft body. Saving a
 * failed/skipped platform resets it back to pending. Returns the refreshed post
 * + draft.
 */
export async function saveDraft(
  id: string,
  platform: Platform,
  partial: Partial<PlatformDraft>,
): Promise<SaveDraftResult> {
  const json = await publisherCall<{ post?: PromotionPost; draft?: PlatformDraft; error?: string }>(
    `/api/publisher/${id}/drafts/${platform}`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(partial),
    },
  );
  if (json.error) throw new Error(json.error);
  return { post: json.post, draft: json.draft };
}

// ── preflight ────────────────────────────────────────────────────

export interface PreflightResult {
  checks: ChannelReadiness[];
  ready_platforms: Platform[];
  blocked_platforms: Platform[];
  can_publish: boolean;
  message?: string;
}

/**
 * POST `/api/publisher/:id/preflight` `{platforms?}`. Returns per-channel
 * readiness plus the ready / blocked split and a `can_publish` gate.
 */
export async function preflight(id: string, platforms?: Platform[]): Promise<PreflightResult> {
  const json = await publisherCall<{
    checks?: ChannelReadiness[];
    ready_platforms?: Platform[];
    blocked_platforms?: Platform[];
    can_publish?: boolean;
    message?: string;
    error?: string;
  }>(`/api/publisher/${id}/preflight`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(platforms ? { platforms } : {}),
  });
  if (json.error) throw new Error(json.error);
  return {
    checks: json.checks ?? [],
    ready_platforms: json.ready_platforms ?? [],
    blocked_platforms: json.blocked_platforms ?? [],
    can_publish: Boolean(json.can_publish),
    message: json.message,
  };
}

// ── publish ──────────────────────────────────────────────────────

/**
 * POST `/api/publisher/:id/publish`. Two shapes:
 *   • `publish(id, 'telegram')` → query `?platform=telegram` (single platform).
 *   • `publish(id, ['telegram','twitter'])` → body `{platforms}` filtered to
 *     pending server-side. A no-op resolves to `{results:[], message:"No
 *     pending platforms to publish"}`.
 *   • `publish(id)` → publishes all pending.
 * Returns the publish results (refresh the post + report failures after).
 */
export interface PublishOutcome {
  results: PublishResult[];
  message?: string;
}

export async function publish(
  id: string,
  platforms?: Platform[] | Platform,
): Promise<PublishOutcome> {
  if (typeof platforms === 'string') {
    const json = await publisherCall<{ results?: PublishResult[]; message?: string; error?: string }>(
      `/api/publisher/${id}/publish?platform=${encodeURIComponent(platforms)}`,
      { method: 'POST' },
    );
    if (json.error) throw new Error(json.error);
    return { results: json.results ?? [], message: json.message };
  }

  const json = await publisherCall<{ results?: PublishResult[]; message?: string; error?: string }>(
    `/api/publisher/${id}/publish`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(platforms ? { platforms } : {}),
    },
  );
  if (json.error) throw new Error(json.error);
  return { results: json.results ?? [], message: json.message };
}

// ── reschedule ───────────────────────────────────────────────────

/**
 * POST `/api/publisher/:id/reschedule` `{scheduled_at}`. A 404 means the route
 * isn't deployed yet — tolerated by the caller (we still throw the `not found`
 * so the UI can decide), but pass `null` to clear the schedule.
 */
export async function reschedule(id: string, scheduledAt: string | null): Promise<PromotionPost> {
  const json = await publisherCall<{ post?: PromotionPost; error?: string }>(
    `/api/publisher/${id}/reschedule`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ scheduled_at: scheduledAt }),
    },
  );
  if (!json.post) throw new Error(json.error ?? 'reschedule failed');
  return json.post;
}

// ── bulk ─────────────────────────────────────────────────────────

export type BulkAction = 'reschedule' | 'delete' | 'duplicate';

export interface BulkItemResult {
  id: string;
  success: boolean;
  error?: string;
  new_id?: string;
}

export interface BulkData {
  scheduled_at?: string | null;
}

/**
 * POST `/api/publisher/bulk` `{action, ids (max 50), data?}`. The worker returns
 * `{ok:true, results}` — but per-item success is reported on `success`, NOT a
 * per-item `ok`. We surface the raw per-item results so the caller can tally.
 */
export async function bulk(
  action: BulkAction,
  ids: string[],
  data?: BulkData,
): Promise<BulkItemResult[]> {
  const json = await publisherCall<{
    ok?: boolean;
    results?: Array<{ id?: string; success?: boolean; error?: string; new_id?: string }>;
    error?: string;
  }>('/api/publisher/bulk', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data ? { action, ids, data } : { action, ids }),
  });
  if (json.error) throw new Error(json.error);
  return (json.results ?? []).map((r) => ({
    id: String(r.id ?? ''),
    success: Boolean(r.success), // per-item success, not `ok`
    error: r.error,
    new_id: r.new_id,
  }));
}

// ── metrics ──────────────────────────────────────────────────────

/** GET `/api/publisher/:id/metrics` → engagement samples for the post. */
export async function postMetrics(id: string): Promise<EngagementSample[]> {
  const json = await publisherCall<{ metrics?: EngagementSample[]; error?: string }>(
    `/api/publisher/${id}/metrics`,
  );
  if (json.error) throw new Error(json.error);
  return json.metrics ?? [];
}

/** POST `/api/publisher/metrics/run` → kicks a metrics scan, returns counts. */
export async function runMetrics(): Promise<{ scanned: number; written: number }> {
  const json = await publisherCall<{ scanned?: number; written?: number; error?: string }>(
    '/api/publisher/metrics/run',
    { method: 'POST' },
  );
  if (json.error) throw new Error(json.error);
  return { scanned: json.scanned ?? 0, written: json.written ?? 0 };
}

// ── queue stage (client-derived — mirrors the old page) ──────────

/**
 * Client-side bucket derivation, byte-for-byte equivalent to the old
 * PromotionPublisherPage.getQueueStage:
 *   - any `failed` OR no statuses at all → attention
 *   - any `publishing` → publishing
 *   - a future `scheduled_at` → scheduled
 *   - all remaining statuses `published`/`skipped` → published
 *   - otherwise → attention
 */
export function getQueueStage(post: PromotionPost): QueueStage {
  const statuses = PLATFORMS.map((platform) => post.status?.[platform]).filter(
    (status): status is PostStatus => Boolean(status),
  );

  if (statuses.some((status) => status === 'failed') || statuses.length === 0) return 'attention';
  if (statuses.some((status) => status === 'publishing')) return 'publishing';

  const scheduledAt = post.scheduled_at ? Date.parse(post.scheduled_at) : NaN;
  if (Number.isFinite(scheduledAt) && scheduledAt > Date.now()) return 'scheduled';

  if (statuses.every((status) => status === 'published' || status === 'skipped')) return 'published';
  return 'attention';
}
